import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, KeyRound, CheckCircle2, XCircle } from "lucide-react";

export const AttomTestCard = () => {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [info, setInfo] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/attom/key").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const run = async () => {
    setBusy(true);
    try { setRes((await api.post("/admin/attom/test")).data); } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const save = async (value) => {
    setSaving(true);
    try {
      setInfo((await api.put("/admin/attom/key", { key: value })).data);
      setKey(""); setRes(null);
      toast.success(value ? "ATTOM accepted the key - it's live now" : "Back to the deployment key");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  return (
    <Card className="mb-5 p-6" data-testid="attom-test-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="mb-1 flex items-center gap-2 font-display font-bold text-slate-900"><KeyRound className="h-4 w-4" /> ATTOM Key</h3>
          <p className="text-xs text-slate-500" data-testid="attom-key-info">
            {info ? `In use: key ending ${info.key_hint || "-"} (${info.source === "admin" ? "saved here" : "from deployment"}).` : ""} Test does one property lookup - no lead run, nobody contacted.
          </p>
        </div>
        <Button variant="outline" onClick={run} disabled={busy} data-testid="attom-test-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test ATTOM key"}
        </Button>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Input type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Paste a new ATTOM API key" data-testid="attom-key-input" />
        <Button onClick={() => save(key.trim())} disabled={saving || key.trim().length < 10} className="gold-cta border-0 font-bold" data-testid="attom-key-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check & save"}
        </Button>
      </div>
      <p className="mt-1 text-xs text-slate-400">The key is checked with ATTOM first and only saved if it works. It takes effect right away - no redeploy needed.
        {info?.source === "admin" && <> <button type="button" className="font-semibold text-amber-700 hover:underline" onClick={() => save("")} data-testid="attom-key-revert-btn">Use the deployment key instead</button></>}
      </p>
      {res && (
        <div className={`mt-4 flex items-start gap-2 rounded-md p-3 text-sm ${res.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="attom-test-result">
          {res.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0" />}
          <span>{res.ok ? "ATTOM key works." : "ATTOM rejected the request."} {res.status ? `HTTP ${res.status}` : ""} {res.message ? `- ${res.message}` : ""} {res.key_hint ? `(key ending ${res.key_hint.replace("…", "")})` : ""}</span>
        </div>
      )}
    </Card>
  );
};
